import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClaimOutletContext, ClaimFileDto } from "../../types";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import DocumentsWorkspace from "../../../../components/DocumentsPanels/DocumentsWorkspace";
import { useDocumentsPreview } from "../../../../components/DocumentsPanels/useDocumentsPreview";
import { deleteClaimFile, downloadClaimFile, loadDocument, updateClaimFile } from "../../api/ClaimFileAPI";
import ClaimFileDetailsPanel from "../common/ClaimFileDetailsPanel";
import { ClaimStatusEnum } from "../../types/ClaimStatusEnum";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { mergeFiles } from "../../../../utils/ClaimFileUtils";

export default function EditClaimDocument() {
    const { t } = useTranslation();
    const { claimUpdate, setClaimUpdate, setHideClaimActionButtons } = useOutletContext<ClaimOutletContext>();
    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });

    const [loading, setLoading] = useState(false);
    const [isDeletingFile, setIsDeletingFile] = useState(false);
    const [readOnly, setReadOnly] = useState<boolean>();
    const [selectedDocument, setSelectedDocument] = useState<ClaimFileDto | null>(null);

    const { showModal, modalData, closeModal, isLoading, errorModal, currentItem, openModal: openDeleteModal } = useModal<ClaimFileDto>();

    const fetchDocumentData = useCallback(async (doc: ClaimFileDto) => {
        const downloaded = await downloadClaimFile(doc.id!);
        return { data: downloaded.data, contentType: downloaded.contentType };
    }, []);

    const preview = useDocumentsPreview<ClaimFileDto>({ fetchDocumentData });

    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode');
    
    useEffect(() => {
        if (!claimUpdate) return;
        setReadOnly(claimUpdate.status === ClaimStatusEnum.Closed || mode == 'view');
    }, [claimUpdate]);

    useEffect(() => {
        return () => setHideClaimActionButtons(false);
    }, [setHideClaimActionButtons]);

    const readFileAsDataUrl = (file: File) =>
        new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });

    const handleBackToList = () => {
        setSelectedDocument(null);
        setHideClaimActionButtons(false);
    };

    const handleFileSaved = (updatedFile: ClaimFileDto) => {
        setClaimUpdate?.((prev) => {
            if (!prev) return prev;

            const nextFiles = (prev.files ?? []).map((file) => {
                if (updatedFile.id != null && file.id != null) {
                    return file.id === updatedFile.id ? { ...file, ...updatedFile } : file;
                }

                return file.fileName === updatedFile.fileName ? { ...file, ...updatedFile } : file;
            });

            return { ...prev, files: nextFiles };
        });
    };

    const handleInfo = (doc: ClaimFileDto) => {
        setSelectedDocument(doc);
        setHideClaimActionButtons(true);
        void preview.previewDocument(doc);
    };

    const toggleStarred = async (doc: ClaimFileDto) => {
        if (!doc.isStarred == false) {
            openDeleteModal(doc, t("claim:modal.modalStarFileTitle"),t("claim:modal.modalStarFileSubtitle"), "deleteWithoutInput", "");
            setIsDeletingFile(false);
        } else {
            if (doc.id == null) return;
            const updated: ClaimFileDto = { ...doc, isStarred: !doc.isStarred };
            try {
                await updateClaimFile(doc.id.toString(), updated);
                setClaimUpdate?.((prev) => {
                    if (!prev) return prev;
                    return { ...prev, files: prev.files.map((f) => f.id === doc.id ? { ...f, isStarred: updated.isStarred } : f) };
                });
            } catch (error) {
                const fetchError = error as ApiErrorResponse;
                showAlert({
                    message: t(`claim:${fetchError?.message?.toString()}`, { name: fetchError?.name ?? "" }),
                    type: "danger",
                    duration: 3000,
                });
            }
        }
    };

    const confirmSubmitModal = async () => {
        try {
            if (modalData.type === "deleteWithoutInput" && !isDeletingFile) {
                if (!currentItem) return;
                const updated = { ...currentItem, isStarred: !currentItem.isStarred };
                await updateClaimFile(currentItem.id?.toString() ?? "", updated);
                setClaimUpdate?.((prev) => {
                    if (!prev) return prev;
                    return { ...prev, files: prev.files.map((f) => f.id === currentItem.id ? { ...f, isStarred: updated.isStarred } : f) };
                });
            }
            if (modalData.type === "deleteWithoutInput" && isDeletingFile) {
                if (!currentItem) return;
                await deleteClaimFile(currentItem.id?.toString() ?? "");

                if ((claimUpdate?.files ?? []).length <= 1) {
                    preview.resetPreview();
                }

                setClaimUpdate?.((prev) => {
                    if (!prev) return prev;
                    const nextFiles = (prev.files ?? []).filter((file) => {
                        if (currentItem.id != null && file.id != null) {
                            return file.id !== currentItem.id;
                        }
                        if (currentItem.fileName) {
                            const nameA = file.fileName ?? file.fileName;
                            return nameA !== currentItem.fileName;
                        }
                        return true;
                    });
                    return { ...prev, files: nextFiles };
                });
                 showAlert({
                    message: t("claim:modal.deleteClaimFileSuccessful"),
                    type: "success",
                    duration: 3000,
                });

            }
            closeModal();
        } catch (error) {
            console.error("confirmSubmitModal error:", error);
        }
    };

    const handleRemoveFile = (doc: ClaimFileDto) => {
        if (doc.id == null && !doc.fileName) return;
        openDeleteModal(doc,t("claim:modal.modalDeleteFileTitle"),t("claim:modal.modalDeleteFileSubtitle"), "deleteWithoutInput", "");
        setIsDeletingFile(true)
    };

    const handleLoadFiles = async (files: File[]) => {
        if (files.length === 0 || !claimUpdate?.id) return;

        const MAX_TOTAL_SIZE = 30_000_000;
        const totalSize = files.reduce((acc, f) => acc + f.size, 0);
        if (totalSize >= MAX_TOTAL_SIZE) {
            showAlert({ message: t("common:documents.fileTooLarge"), type: "danger", duration: 3000 });
            return;
        }

        setLoading(true);
        try {
            const updatedFiles = await loadDocument({ files, claimId: claimUpdate.id });
            setClaimUpdate((prev) => {
                if (!prev) return prev;
                return { ...prev, files: mergeFiles(Array.isArray(updatedFiles) ? updatedFiles : [], prev.files ?? []) };
            });

            await Promise.all(
                files.map(async (file) => {
                    const data = await readFileAsDataUrl(file);
                    preview.cacheDocumentData({ fileName: file.name }, { data, contentType: file.type });
                })
            );

            const firstFile = files[0];
            if (firstFile) {
                void preview.previewDocument({ fileName: firstFile.name, isStarred: false });
            }

            showAlert({ message: t("claim:uploadFileDetailSuccess"), type: "success", duration: 2000 });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: t(`claim:${fetchError?.message?.toString()}`, { name: fetchError?.name ?? "" }),
                type: "danger",
                duration: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <ModalGlobal
                show={showModal}
                onClose={() => {
                    setIsDeletingFile(false);
                    closeModal();
                }}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                onSave={confirmSubmitModal}
                isLoading={isLoading}
            />
            {isVisible && <AlertMessage message={alert!.message} type={alert!.type} onClose={hideAlert} />}

            <DocumentsWorkspace<ClaimFileDto>
                documents={claimUpdate?.files ?? []}
                preview={preview}
                sectionPermission={SectionPermissionTypeEnum.CLAIMS}
                loading={loading}
                disabledActions={readOnly ?? false}
                onLoadFiles={handleLoadFiles}
                onInfo={handleInfo}
                onDelete={handleRemoveFile}
                onStar={toggleStarred}
                autoPreviewKey={claimUpdate?.id ?? null}
                selectInitialDocument={(documents) =>
                    documents.find((doc) => doc.isAnalizedByIA) ??
                    documents.find((doc) => doc.isStarred) ??
                    documents[0]
                }
                detailsPanel={selectedDocument
                    ? <ClaimFileDetailsPanel file={selectedDocument} onBack={handleBackToList} onSaved={handleFileSaved} />
                    : undefined}
            />
        </>
    );
}
